import { Printer } from 'lucide-react';
import { btnSecundario, pesos, tarjeta } from '../../components/ui/estilos';

const n = (v: any) => parseFloat(v || 0) || 0;
const hora = (iso: string) => new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false });
const card: React.CSSProperties = { ...tarjeta, padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 10 };
const titulo: React.CSSProperties = { margin: 0, fontSize: 16, fontWeight: 700 };
const fila: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', fontSize: 16, padding: '4px 0' };
const enlace: React.CSSProperties = { alignSelf: 'flex-start', height: 40, padding: '0 4px', border: 'none', background: 'transparent', fontSize: 15, fontWeight: 700, cursor: 'pointer', color: 'var(--brand-texto)' };
const chico: React.CSSProperties = { ...btnSecundario, height: 38, padding: '0 14px', fontSize: 14, borderRadius: 9 };

interface Props {
  resumen: any;
  efectivoEsperado: number;
  porMetodo: { clave: string; label: string; total: number }[];
  etiquetaCategoria: (v?: string) => string;
  historial: any[];
  ultimoTicket: any | null;
  onEntrada: () => void; onSalida: () => void; onCancelarMov: (mv: any) => void;
  onRetirarPropina: (p: any) => void; onImprimirPropinas: () => void;
  onVerCortes: () => void; onVerTickets: () => void;
}

export default function TurnoAbierto(p: Props) {
  const r = p.resumen;
  const propinas: any[] = r.propinas_pendientes || [];
  const tickets = (r.cuentas_normales || 0) + (r.cuentas_con_descuento || 0);
  const movs: any[] = r.movimientos || [];
  const com = r.comisiones;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(220px, 100%), 1fr))', gap: 12 }}>
        {[['Ventas del turno', pesos(n(r.total_ventas))], ['Tickets cobrados', String(tickets)], ['Efectivo esperado', pesos(p.efectivoEsperado)]].map(([k, v]) => (
          <div key={k} style={{ ...tarjeta, borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.03em', color: 'var(--muted)', textTransform: 'uppercase' }}>{k}</span>
            <span style={{ fontSize: 26, fontWeight: 700 }}>{v}</span>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(340px, 100%), 1fr))', gap: 18, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <section style={card} aria-labelledby="t-metodo">
            <h2 id="t-metodo" style={titulo}>Ventas por método</h2>
            {p.porMetodo.map(m => <div key={m.clave} style={fila}><span style={{ color: 'var(--muted)' }}>{m.label}</span><span style={{ fontWeight: 700 }}>{pesos(m.total)}</span></div>)}
          </section>

          <section style={{ ...card, gap: 12 }} aria-labelledby="t-mov">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
              <h2 id="t-mov" style={titulo}>Movimientos de efectivo</h2>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={p.onEntrada} style={chico}>+ Entrada</button>
                <button onClick={p.onSalida} style={chico}>+ Salida</button>
              </div>
            </div>
            {!movs.length && <div style={{ fontSize: 15, color: 'var(--muted)', padding: '8px 0' }}>Sin movimientos aparte de las ventas.</div>}
            {movs.map(mv => {
              // La comisión de propina no mueve efectivo: baja el saldo del mesero.
              const comision = mv.tipo === 'comision_propina', entrada = mv.tipo === 'deposito';
              return (
                <div key={mv.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--line-2)' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700 }}>{mv.motivo}</div>
                    <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                      {hora(mv.created_at)} · {comision ? 'comisión de propina, no sale de caja' : p.etiquetaCategoria(mv.categoria)}
                    </div>
                  </div>
                  <span style={{ fontSize: 16, fontWeight: 700, color: comision ? 'var(--warn)' : entrada ? 'var(--ok)' : 'var(--danger)' }}>{entrada ? '+' : '−'}{pesos(n(mv.monto))}</span>
                  {!comision && <button onClick={() => p.onCancelarMov(mv)} aria-label={`Cancelar movimiento ${mv.motivo}`} title="Cancelar movimiento" style={{ ...chico, width: 38, padding: 0 }}>✕</button>}
                </div>
              );
            })}
          </section>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <section style={card} aria-labelledby="t-prop">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 id="t-prop" style={titulo}>Propinas por mesero</h2>
              {propinas.some(x => (x.a_entregar ?? x.pendiente) > 0.005) && (
                <button onClick={p.onImprimirPropinas} aria-label="Imprimir propinas" title="Imprimir propinas" style={{ ...chico, width: 38, padding: 0 }}><Printer size={18} aria-hidden style={{ display: 'block', margin: '0 auto' }} /></button>
              )}
            </div>
            {!propinas.length && <div style={{ fontSize: 15, color: 'var(--muted)' }}>Sin propinas en este turno.</div>}
            {propinas.map(x => {
              const entregar = x.a_entregar ?? x.pendiente;
              const extra = [
                Math.abs(x.saldo_anterior) > .005 ? `arrastre ${pesos(x.saldo_anterior)}` : '',
                x.retirado_turno > .005 ? `retirado ${pesos(x.retirado_turno)}` : '',
                x.comision_turno > .005 ? `comisión −${pesos(x.comision_turno)}` : '',
              ].filter(Boolean).join(' · ');
              return (
                <div key={x.mesero_id || x.mesero} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0' }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 16, color: 'var(--muted)' }}>{x.mesero}</span>
                    {extra && <span style={{ fontSize: 13, color: 'var(--faint)' }}>{extra}</span>}
                  </span>
                  <span style={{ fontSize: 16, fontWeight: 700 }}>{pesos(entregar)}</span>
                  {entregar > .005
                    ? <button onClick={() => p.onRetirarPropina({ ...x, monto: entregar })} style={chico}>Entregar</button>
                    : <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ok)', width: 84, textAlign: 'center' }}>Entregada</span>}
                </div>
              );
            })}
          </section>

          <section style={card} aria-labelledby="t-cortes">
            <h2 id="t-cortes" style={titulo}>Cortes anteriores</h2>
            {p.historial.filter(t => t.cierre_at).slice(0, 3).map(t => {
              const dif = n(t.diferencia);
              return (
                <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: 9, border: '1px solid var(--line)', background: 'var(--bg)' }}>
                  <span style={{ fontSize: 14 }}>{new Date(t.cierre_at).toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' })} · {t.usuario_cierre || t.usuario_apertura}</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: Math.abs(dif) < .005 ? 'var(--ok)' : 'var(--danger)' }}>{Math.abs(dif) < .005 ? 'Exacto' : `${dif > 0 ? '+' : '−'}${pesos(Math.abs(dif))}`}</span>
                    <span style={{ fontSize: 14, fontWeight: 700 }}>{pesos(n(t.total_ventas))}</span>
                  </span>
                </div>
              );
            })}
            <button onClick={p.onVerCortes} style={enlace}>Ver historial completo de cortes →</button>
          </section>
          <section style={card} aria-labelledby="t-tickets">
            <h2 id="t-tickets" style={titulo}>Tickets</h2>
            {p.ultimoTicket && (
              <button onClick={p.onVerTickets} style={{ textAlign: 'left', padding: '10px 12px', borderRadius: 9, border: '1px solid var(--line)', background: 'var(--bg)', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 2, color: 'var(--ink)' }}>
                <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.02em', color: 'var(--muted)' }}>ÚLTIMO TICKET</span>
                <span style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 15, fontWeight: 700 }}>
                  <span>{p.ultimoTicket.mesa_numero ? `Mesa ${p.ultimoTicket.mesa_numero}` : 'Para llevar'}</span><span>{pesos(n(p.ultimoTicket.monto_total))}</span>
                </span>
                <span style={{ fontSize: 13, color: 'var(--muted)' }}>{p.ultimoTicket.mesero} · {hora(p.ultimoTicket.created_at)}</span>
              </button>
            )}
            <button onClick={p.onVerTickets} style={enlace}>Ver historial de tickets →</button>
          </section>
        </div>
      </div>

      {com?.activo && com.total_comision > 0 && (
        <section style={card} aria-labelledby="t-com">
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
            <h2 id="t-com" style={titulo}>Comisiones de terminal</h2>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--muted)' }}>{com.pct_efectivo}% efectivo{com.fijo > 0 ? ` + ${pesos(com.fijo)} por cobro` : ''}</span>
          </div>
          <div style={{ fontSize: 14, color: 'var(--muted)' }}>Se calcula sobre lo cobrado en terminal, propina incluida. No afecta las ventas del turno.</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '12px 20px', paddingTop: 6, fontSize: 15 }}>
            {com.detalle.map((d: any) => [
              <span key={d.metodo + 'a'} style={{ fontWeight: 700, textTransform: 'capitalize' }}>{d.metodo} <span style={{ fontWeight: 400, color: 'var(--muted)' }}>{d.transacciones} {d.transacciones === 1 ? 'cobro' : 'cobros'}</span></span>,
              <span key={d.metodo + 'b'} style={{ color: 'var(--muted)' }}>{pesos(d.base)}</span>,
              <span key={d.metodo + 'c'} style={{ color: 'var(--warn)' }}>−{pesos(d.comision)}</span>,
              <span key={d.metodo + 'd'} style={{ fontWeight: 700, justifySelf: 'end' }}>{pesos(d.neto)}</span>,
            ])}
            {[['Neto a depositar', { fontWeight: 700 }], [pesos(com.total_base), { color: 'var(--muted)' }], [`−${pesos(com.total_comision)}`, { color: 'var(--warn)' }], [pesos(com.total_neto), { fontWeight: 700, justifySelf: 'end', color: 'var(--ok)' }]]
              .map(([t, s], i) => <span key={i} style={{ paddingTop: 8, borderTop: '1px solid var(--line)', ...(s as object) }}>{t as string}</span>)}
          </div>
          {com.absorbe < 100 && (
            <div style={{ fontSize: 14, color: 'var(--muted)' }}>
              El negocio absorbe {pesos(com.comision_negocio)} ({com.absorbe}%); se descuentan {pesos(com.comision_meseros)} de propinas.
            </div>
          )}
        </section>
      )}
    </div>
  );
}
